/**
 * Predictive search overlay.
 * As the user types, a debounced request renders the `predictive-search`
 * section (via the /search route with `view=predictive`, which returns just the
 * results fragment) below the input. Enter / submit falls through to the native
 * form → full /search page. Using the /search page route (not the JSON suggest
 * API) keeps it working under `shopify theme dev`, which does not authorize the
 * storefront JSON APIs.
 *
 * Focus into the input on open (and back to the trigger on close) is handled by
 * drawers.js so it happens synchronously within the user gesture.
 */
(function () {
  var overlay = document.querySelector('[data-search-drawer]');
  if (!overlay) return;

  var panel = overlay.querySelector('.search-overlay__panel');
  var input = overlay.querySelector('[data-search-input]');
  var results = overlay.querySelector('[data-search-results]');
  var status = overlay.querySelector('[data-search-status]');
  if (!input || !results) return;

  var MIN_CHARS = 2;
  var DEBOUNCE = 220;

  var timer = null;
  var lastQuery = '';
  var activeIndex = -1;

  function endpoint(q) {
    return '/search?q=' + encodeURIComponent(q) + '&type=product&view=predictive';
  }

  function announce(msg) {
    if (status) status.textContent = msg;
  }

  function setActive(index) {
    var items = results.querySelectorAll('.search__item');
    activeIndex = index;
    items.forEach(function (it, i) {
      var on = i === index;
      it.classList.toggle('is-active', on);
      it.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (index > -1 && items[index]) {
      input.setAttribute('aria-activedescendant', items[index].id);
      items[index].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function clearResults() {
    results.innerHTML = '';
    activeIndex = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  function runSearch(q) {
    fetch(endpoint(q), { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('search ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (q !== lastQuery) return; // a newer query superseded this one
        results.innerHTML = html;
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        var count = results.querySelectorAll('.search__item').length;
        input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
        announce(count > 0 ? count + ' result' + (count === 1 ? '' : 's') : 'No products found');
      })
      .catch(function () {
        if (q !== lastQuery) return;
        clearResults();
      });
  }

  input.addEventListener('input', function () {
    var q = input.value.trim();
    lastQuery = q;
    clearTimeout(timer);
    if (q.length < MIN_CHARS) {
      clearResults();
      announce('');
      return;
    }
    timer = setTimeout(function () { runSearch(q); }, DEBOUNCE);
  });

  /* Arrow keys move a highlight through the results; Enter opens the highlight */
  input.addEventListener('keydown', function (e) {
    var items = results.querySelectorAll('.search__item');
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      var next = activeIndex + (e.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) next = items.length - 1;
      if (next >= items.length) next = 0;
      setActive(next);
    } else if (e.key === 'Enter' && activeIndex > -1 && items[activeIndex]) {
      var link = items[activeIndex].querySelector('a');
      if (link) { e.preventDefault(); window.location.href = link.getAttribute('href'); }
    }
  });

  /* Focus trap: keep Tab within the modal panel while the overlay is open */
  overlay.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !panel) return;
    var focusables = panel.querySelectorAll('input:not([type="hidden"]), a[href], button');
    var visible = Array.prototype.filter.call(focusables, function (el) {
      return el.offsetWidth || el.offsetHeight || el === document.activeElement;
    });
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* Reset state when the overlay closes (drawers.js toggles .is-open and moves
     focus back to the trigger). */
  new MutationObserver(function () {
    if (!overlay.classList.contains('is-open')) {
      clearTimeout(timer);
      input.value = '';
      clearResults();
      announce('');
      lastQuery = '';
    }
  }).observe(overlay, { attributes: true, attributeFilter: ['class'] });
})();
